import * as amplitude from '@amplitude/analytics-browser';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import * as Sentry from '@sentry/react';
import { useEffect, useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import invariant from 'tiny-invariant';
import { match, Pattern } from 'ts-pattern';

import type { ActivityId, UserLanguage } from '~/api/user/types';

import { useRegister } from '~/api/auth';
import { FetchError, HttpError, HttpTooManyRequestsError } from '~/api/errors';
import { Button, ErrorMessage, ExternalLink, Redirect } from '~/components';
import { Combobox, Field, Form, Radio, Select, Textarea } from '~/components/Form';
import { AmplitudeEventName, AmplitudeUserProperty } from '~/constants/amplitude';
import { Activity, availableActivities, availablePrimaryActivities, MainOccupation } from '~/constants/constants';
import { useLeadJourneyClickId } from '~/hooks/useLeadJourneyClickId';
import { useColorThemeContext } from '~/providers/ColorThemeProvider/ColorThemeProvider.context';
import { routes } from '~/providers/RouterProvider/router.routes';
import { parsePhoneNumber } from '~/utils/libphonenumberjs';
import { isNotBlank } from '~/utils/string';

import type { BusinessInformationFormFields } from './RegistrationBusinessInformation.types';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { PreviousStepButton } from './PreviousStepButton';
import { useRegistrationContext } from './Registration.context';
import {
  buttonsStyle,
  buttonStyle,
  contentWrapperStyle,
  formStyle,
  primaryActivityCardsStyle,
  primaryActivityLegendStyle,
} from './RegistrationBusinessInformation.css';
import { RegistrationAddReferralCodeButton } from './RegistrationReferralCode/RegistrationAddReferralCodeButton';
import { RegistrationReferralCode } from './RegistrationReferralCode/RegistrationReferralCode';
import { checkAccountInformationStepCompleted, checkVatInformationStepCompleted } from './utils';

export const RegistrationBusinessInformation = () => {
  const mutation = useRegister();

  const { accountInformation, businessInformation, referralCodeInformation, setBusinessInformation, setUser, vatInformation } =
    useRegistrationContext();

  const { control, getValues, handleSubmit, setValue, watch } = useForm<BusinessInformationFormFields>({
    defaultValues: businessInformation,
  });
  const formId = useId();

  const watchPrimaryActivity = watch('primaryActivity');
  const isOtherActivitySelected = watch('secondaryActivities')[`a${Activity.other}`];

  const [ref1] = useAutoAnimate();
  const [ref2] = useAutoAnimate();
  const { clickId } = useLeadJourneyClickId();
  const { colorTheme } = useColorThemeContext();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation(['auth', 'common', 'validation']);

  const secondaryActivityOptions = useMemo(
    () =>
      availableActivities
        .filter((activity) => activity !== watchPrimaryActivity)
        .map((activity) => ({ value: activity, label: t(`common:activities.${activity}.label`) })),
    [t, watchPrimaryActivity],
  );
  const secondaryActivityItems = Combobox.useItems(secondaryActivityOptions, {
    getLabel: (option) => option.label,
    getValue: (option) => option.value,
  });

  // Uncheck the secondary activity if it has been selected as primary activity
  useEffect(() => {
    if (watchPrimaryActivity) setValue('secondaryActivities', { ...getValues('secondaryActivities'), [`a${watchPrimaryActivity}`]: false });
  }, [getValues, setValue, watchPrimaryActivity]);

  // Clear the other information when the other activity isn't checked
  useEffect(() => {
    if (!isOtherActivitySelected) setValue('otherActivityInformation', '');
  }, [isOtherActivitySelected, setValue]);

  if (!checkVatInformationStepCompleted(vatInformation)) return <Redirect to={routes.register} />;
  if (!checkAccountInformationStepCompleted(accountInformation)) return <Redirect to={routes.registerAccountInformation} />;

  const mainOccupationItems = [
    { label: t('auth:registrationPage.stepBusinessInformation.form.mainOccupationField.options.mainOccupation'), value: true },
    { label: t('auth:registrationPage.stepBusinessInformation.form.mainOccupationField.options.secondaryOccupation'), value: false },
  ];

  const onSubmit = handleSubmit((data) => {
    setBusinessInformation(data);

    // These fields can't be null after successful validation.
    invariant(data.mainOccupation !== null, 'Expected main occupation to be defined');
    invariant(data.primaryActivity !== null, 'Expected primary activity to be defined');

    mutation.mutate(
      {
        vatNumber: vatInformation.vatNumber,
        emailAddress: accountInformation.emailAddress,
        password: accountInformation.password,
        phoneNumber: match(accountInformation.callingCode)
          .with(
            '32',
            () =>
              parsePhoneNumber(`+${accountInformation.callingCode}${accountInformation.phoneNumber}`)?.format('E.164') ??
              `+${accountInformation.callingCode}${accountInformation.phoneNumber}`,
          )
          .with('31', () => `+${accountInformation.callingCode}${accountInformation.phoneNumber}`)
          .with(Pattern.any, () => accountInformation.phoneNumber)
          .exhaustive(),
        mainOccupation: data.mainOccupation,
        primaryActivity: data.primaryActivity,
        secondaryActivities: Object.entries(data.secondaryActivities)
          .filter(([, isSelected]) => isSelected)
          .map(([activity]) => +activity.slice(1) as ActivityId), // Remove the `a` prefix
        otherActivityInformation: data.otherActivityInformation,
        language: i18n.language as UserLanguage,
        referralCode: referralCodeInformation?.code ?? null,
        colorCombo: colorTheme,
        leadJourneyClickId: clickId,
      },
      {
        onSuccess: (user) => {
          setUser(user);

          Sentry.setUser({
            id: user.id,
            username: `${user.firstName} ${user.lastName}`,
            email: user.email,
            ip_address: '{{auto}}',
          });

          const amplitudeIdentifyEvent = new amplitude.Identify();
          amplitudeIdentifyEvent.set(AmplitudeUserProperty.MainOccupation, user.mainOccupation === MainOccupation.yes);
          amplitude.identify(amplitudeIdentifyEvent);
          amplitude.track(AmplitudeEventName.RegistrationStep3Completed);

          navigate(routes.registerSuccess);
        },
      },
    );
  });

  return (
    <AuthLayoutContent>
      <PreviousStepButton to="accountInformation" />

      <AuthLayoutTitleGroup
        eyebrow={t('auth:registrationPage.common.header.pill')}
        subtitle={t('auth:registrationPage.stepBusinessInformation.header.subtitle')}
        title={t('auth:registrationPage.stepBusinessInformation.header.title', {
          name: vatInformation?.founder?.firstName ?? vatInformation?.companyName ?? '',
        })}
      />

      <div className={contentWrapperStyle} ref={ref1}>
        <Form id={formId} onSubmit={onSubmit}>
          <div className={formStyle} ref={ref2}>
            <Controller
              control={control}
              name="mainOccupation"
              render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                  <Field.Label>{t('auth:registrationPage.stepBusinessInformation.form.mainOccupationField.label')}</Field.Label>

                  <Select.Root items={mainOccupationItems} onValueChange={onChange} value={value}>
                    <Select.Button autoFocus placeholder={t('common:placeholders.select')} />

                    <Select.List>
                      {mainOccupationItems.map((item) => (
                        <Select.Item key={String(item.value)} value={item.value}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Root>

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{ validate: (value) => value !== null || t('validation:required') }}
            />

            <Controller
              control={control}
              name="primaryActivity"
              render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                  <Radio.Group onValueChange={onChange} value={value ?? null}>
                    <Radio.GroupLegend className={primaryActivityLegendStyle}>
                      {t('auth:registrationPage.stepBusinessInformation.form.primaryActivityField.label')}
                    </Radio.GroupLegend>

                    <div className={primaryActivityCardsStyle}>
                      {availablePrimaryActivities.map((activity) => (
                        <Radio.Root key={activity} value={activity}>
                          <Radio.Label>{t(`common:activities.${activity}.label`)}</Radio.Label>
                          <Radio.Description>{t(`common:activities.${activity}.description`)}</Radio.Description>
                        </Radio.Root>
                      ))}
                    </div>
                  </Radio.Group>

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{ required: { value: true, message: t('validation:required') } }}
            />

            <Controller
              control={control}
              name="secondaryActivities"
              render={({ field: { name, onChange, value } }) => (
                <Field.Root name={name}>
                  <Field.Label>{t('auth:registrationPage.stepBusinessInformation.form.secondaryActivityField.label')}</Field.Label>

                  <Combobox.Root
                    items={secondaryActivityItems}
                    multiple
                    onValueChange={(selectedActivities) =>
                      onChange(
                        Object.fromEntries(availableActivities.map((activity) => [`a${activity}`, selectedActivities.includes(activity)])),
                      )
                    }
                    value={secondaryActivityOptions.filter((option) => value[`a${option.value}`]).map((option) => option.value)}
                  >
                    <Combobox.ChipsButton
                      getLabel={(activity: ActivityId) => t(`common:activities.${activity}.label`)}
                      placeholder={t('common:placeholders.select')}
                    />

                    <Combobox.List>
                      {(item: (typeof secondaryActivityOptions)[number]) => (
                        <Combobox.Item key={item.value} value={item.value}>
                          {item.label}
                        </Combobox.Item>
                      )}
                    </Combobox.List>
                  </Combobox.Root>
                </Field.Root>
              )}
            />

            {isOtherActivitySelected && (
              <Controller
                control={control}
                name="otherActivityInformation"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                  <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                    <Field.Label>{t('auth:registrationPage.stepBusinessInformation.form.otherActivitiesField.label')}</Field.Label>
                    <Textarea onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
                rules={{
                  required: { value: true, message: t('validation:required') },
                  validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                }}
              />
            )}
          </div>
        </Form>

        <RegistrationReferralCode />

        {mutation.isError && (
          <ErrorMessage>
            {match(mutation.error)
              .with(Pattern.instanceOf(HttpTooManyRequestsError), () => t('common:errors.tooManyRequests'))
              .with(Pattern.instanceOf(HttpError), () => (
                <Trans components={{ email: <ExternalLink /> }} i18nKey="auth:registrationPage.stepBusinessInformation.errors.httpError" />
              ))
              .with(Pattern.instanceOf(FetchError), () => t('common:errors.fetchError'))
              .otherwise(() => t('auth:registrationPage.stepBusinessInformation.errors.fallback'))}
          </ErrorMessage>
        )}

        <div className={buttonsStyle}>
          <RegistrationAddReferralCodeButton className={buttonStyle} />

          <Button className={buttonStyle} form={formId} loading={mutation.isPending} type="submit">
            {t('auth:registrationPage.stepBusinessInformation.form.submitButton')}
          </Button>
        </div>
      </div>
    </AuthLayoutContent>
  );
};
